/**
 * Editorial source-of-truth for Hive Homes' community pages.
 *
 * Each entry powers:
 *   1. The thumbnail / one-liner shown on the homepage and `/areas`.
 *   2. The dedicated `/areas/<slug>` long-form community page.
 *   3. The SEO title/description for each community page.
 *
 * Slugs match `slugify(name)` from `src/server/properties-store.ts`, so
 * URLs stay aligned with whatever the live store exposes via
 * `/api/communities`.
 */

export interface CommunityContent {
  slug: string;
  name: string;
  /** One-liner used on the homepage / communities list row. */
  blurb: string;
  /** Short highlight badge used in community list rows. */
  highlight: string;
  /**
   * Page headline shown on `/areas/<slug>`. Usually `Properties for Sale &
   * Rent in <Name>, Dubai` style.
   */
  pageHeadline: string;
  /** SEO title (under 60 chars where practical). */
  seoTitle: string;
  /** SEO description (under 160 chars where practical). */
  seoDescription: string;
  /** Editorial paragraphs (2). */
  paragraphs: string[];
  /** Stats bar — optional, all strings to keep flexibility. */
  statsBar?: { propertyTypes?: string; avgYield?: string };
  /** CTA button label, e.g. "Speak to a JVC Specialist". */
  ctaLabel: string;
  /** Default cover used as a placeholder until the real photo is ready. */
  fallbackCover: string;
}

/**
 * Brand photography for community covers, served from
 * `public/images/communities/` (copied into `dist/images/` at build
 * time). Keyed by the same slugs `slugify()` produces server-side, so
 * live-feed communities without an editorial entry still get their
 * real photo instead of a listing snapshot. A few alias keys guard
 * against minor feed-name variations.
 */
const COMMUNITY_PHOTOS: Record<string, string> = {
  "al-furjan": "/images/communities/al-furjan.jpg",
  arjan: "/images/communities/arjan.jpg",
  "city-walk": "/images/communities/city-walk.jpg",
  "damac-hills-2": "/images/communities/damac-hills-2.jpg",
  "damac-hills": "/images/communities/damac-hills-2.jpg",
  "damac-lagoons": "/images/communities/damac-lagoons.jpg",
  "dubai-creek-harbour-the-lagoons":
    "/images/communities/dubai-creek-harbour-the-lagoons.jpg",
  "dubai-creek-harbour": "/images/communities/dubai-creek-harbour-the-lagoons.jpg",
  "dubai-creek-harbor": "/images/communities/dubai-creek-harbour-the-lagoons.jpg",
  "dubai-harbour": "/images/communities/dubai-harbour.jpg",
  "dubai-investment-park-dip": "/images/communities/dubai-investment-park-dip.jpg",
  "dubai-investment-park": "/images/communities/dubai-investment-park-dip.jpg",
  "dubai-science-park": "/images/communities/dubai-science-park.jpg",
  "expo-city": "/images/communities/expo-city.jpg",
  "jebel-ali": "/images/communities/jebel-ali.jpg",
  "jumeirah-lake-towers": "/images/communities/jumeirah-lake-towers.jpg",
  jlt: "/images/communities/jumeirah-lake-towers.jpg",
  "jumeirah-village-circle": "/images/communities/jumeirah-village-circle.jpg",
  jvc: "/images/communities/jumeirah-village-circle.jpg",
  meydan: "/images/communities/meydan.jpg",
  mudon: "/images/communities/mudon.jpg",
  rukan: "/images/communities/rukan.jpg",
  "tilal-al-ghaf": "/images/communities/tilal-al-ghaf.jpg",
  "town-square": "/images/communities/town-square.jpg",
  "town-square-dubai": "/images/communities/town-square.jpg",
  "umm-suqeim": "/images/communities/umm-suqeim.jpg",
  zabeel: "/images/communities/zabeel.jpg",
};

/** Mirrors `slugify` in `src/server/properties-store.ts`. */
function toSlug(s: string): string {
  return s
    .toLowerCase()
    .replace(/[\s/]+/g, "-")
    .replace(/[^a-z0-9\-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

/** Brand photo for a community, looked up by slug or display name. */
export function findCommunityPhoto(
  identifier: string | undefined,
): string | undefined {
  if (!identifier) return undefined;
  return COMMUNITY_PHOTOS[identifier] ?? COMMUNITY_PHOTOS[toSlug(identifier)];
}

export const COMMUNITIES: CommunityContent[] = [
  {
    slug: "jumeirah-village-circle",
    name: "Jumeirah Village Circle",
    blurb:
      "Modern living, central location. Smart for first-time buyers and high-yield investors.",
    highlight: "7-9% avg yield",
    pageHeadline:
      "Properties for Sale & Rent in Jumeirah Village Circle, Dubai",
    seoTitle:
      "Property for Sale & Rent in Jumeirah Village Circle Dubai | Hive Homes",
    seoDescription:
      "Browse properties for sale and rent in Jumeirah Village Circle Dubai. Studios, 1, 2 and 3-bed apartments. Modern living, strong rental yields. Hive Homes.",
    paragraphs: [
      "Jumeirah Village Circle, known across Dubai as JVC, is one of the city's most popular communities for buyers and investors. Centrally located with direct access to Sheikh Mohammed Bin Zayed Road and Al Khail Road, JVC offers modern apartments at competitive prices without sacrificing quality or connectivity.",
      "The community is home to studios, one, two, and three-bedroom apartments, plus townhouses and a small number of villas. Rental yields typically sit between 7% and 9%, making JVC one of the strongest buy-to-let communities in Dubai.",
    ],
    statsBar: {
      propertyTypes: "Apartments, Studios, Townhouses",
      avgYield: "7–9%",
    },
    ctaLabel: "Speak to a JVC Specialist",
    // Brand aerial photo of JVC's radial street layout.
    fallbackCover: "/images/communities/jumeirah-village-circle.jpg",
  },
  {
    slug: "business-bay",
    name: "Business Bay",
    blurb:
      "Canal-front urban energy, steps from Downtown Dubai and the Burj Khalifa.",
    highlight: "Canal-front living",
    pageHeadline: "Properties for Sale & Rent in Business Bay, Dubai",
    seoTitle: "Property for Sale & Rent in Business Bay Dubai | Hive Homes",
    seoDescription:
      "Explore properties for sale and rent in Business Bay Dubai. Canal-front apartments and studios near Downtown Dubai. Expert advice from Hive Homes boutique brokerage.",
    paragraphs: [
      "Business Bay sits at the centre of Dubai's ambition, a canal-front district bordering Downtown Dubai and the Burj Khalifa, offering some of the most dramatic urban views in the city. The community ranges from compact studios to expansive canal-facing penthouses, attracting both owner-occupiers and investors.",
      "With the Dubai Canal as its defining feature and ongoing development across multiple plots, Business Bay continues to evolve. Properties here command a premium for canal views, and the area has seen consistent price growth as infrastructure and amenities have matured.",
    ],
    statsBar: {
      propertyTypes: "Studios, Apartments, Penthouses",
    },
    ctaLabel: "Speak to a Business Bay Specialist",
    // Downtown / Business Bay skyline and highway at sunrise.
    fallbackCover:
      "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?auto=format&fit=crop&w=1600&q=80",
  },
  {
    slug: "damac-lagoons",
    name: "Damac Lagoons",
    blurb:
      "Off-plan crystal lagoon community. Resort-style family living.",
    highlight: "Lagoon villas",
    pageHeadline: "Off-Plan Property in Damac Lagoons, Dubai",
    seoTitle:
      "Off-Plan Property in Damac Lagoons Dubai | Villas & Townhouses | Hive Homes",
    seoDescription:
      "Browse off-plan villas and townhouses in Damac Lagoons Dubai. Crystal lagoon community from AED 3M. Family living with resort-style amenities. Hive Homes.",
    paragraphs: [
      "Damac Lagoons is a master-planned community built around crystal-clear lagoons and a Mediterranean-inspired lifestyle. Located in Dubailand, the development offers villas and townhouses across multiple clusters, each themed around a different European destination, from Ibiza to Venice to Costa Brava.",
      "For families looking for space, a resort-style environment, and the security of buying from an established developer, Damac Lagoons offers one of the most distinctive propositions in Dubai's off-plan market.",
    ],
    statsBar: {
      propertyTypes: "Villas, Townhouses",
    },
    ctaLabel: "Speak to a Damac Lagoons Specialist",
    // Brand aerial photo of the Damac Lagoons crystal lagoon clusters.
    fallbackCover: "/images/communities/damac-lagoons.jpg",
  },
  {
    slug: "tilal-al-ghaf",
    name: "Tilal Al Ghaf",
    blurb:
      "Dubai's most talked-about new address. A lagoon lifestyle community by Majid Al Futtaim.",
    highlight: "Lagoon lifestyle",
    pageHeadline: "Properties for Sale in Tilal Al Ghaf, Dubai",
    seoTitle:
      "Property for Sale in Tilal Al Ghaf Dubai | Villas & Townhouses | Hive Homes",
    seoDescription:
      "Browse villas and townhouses for sale in Tilal Al Ghaf Dubai by Majid Al Futtaim. 70,000 sqm lagoon, private beach and open green space. Hive Homes Dubai.",
    paragraphs: [
      "Tilal Al Ghaf has become one of the most sought-after addresses in Dubai. Developed by Majid Al Futtaim, the community is built around a stunning 70,000 sqm lagoon, with a private beach, water sports, cycling tracks, and over 30 hectares of open green space.",
      "Villas and townhouses here move quickly, and the community has seen strong secondary market demand from buyers who missed earlier launch phases. Our team has current knowledge of what is available on both the primary and secondary market.",
    ],
    statsBar: {
      propertyTypes: "Villas, Townhouses",
    },
    ctaLabel: "Speak to a Tilal Al Ghaf Specialist",
    // Brand photo of the Tilal Al Ghaf community entrance.
    fallbackCover: "/images/communities/tilal-al-ghaf.jpg",
  },
  {
    slug: "arjan",
    name: "Arjan",
    blurb:
      "High-yield apartments in a well-connected neighbourhood. Ideal for investors and young professionals.",
    highlight: "Investor entry point",
    pageHeadline: "Properties for Sale & Rent in Arjan, Dubai",
    seoTitle: "Property for Sale & Rent in Arjan Dubai | Hive Homes",
    seoDescription:
      "Discover properties for sale and rent in Arjan Dubai. High-yield apartments near Al Barsha. Strong ROI, modern buildings, growing community. Hive Homes Dubai.",
    paragraphs: [
      "Arjan is one of Dubai's best-performing communities for buy-to-let investors. Located in Dubailand with strong connections to Al Barsha, Motor City, and the Miracle Garden, Arjan has a growing selection of new developments from trusted developers including Danube.",
      "The community appeals to young professionals and small families who want a well-designed home at a sensible price. Studios and one-bedroom apartments dominate the market, with consistent rental demand driven by proximity to major employment hubs.",
    ],
    statsBar: {
      propertyTypes: "Studios, Apartments",
    },
    ctaLabel: "Speak to an Arjan Specialist",
    // Brand photo of Arjan.
    fallbackCover: "/images/communities/arjan.jpg",
  },
  {
    slug: "umm-suqeim",
    name: "Umm Suqeim",
    blurb:
      "Established beachside neighbourhood with Burj Al Arab views.",
    highlight: "Beachside villas",
    pageHeadline: "Properties for Sale & Rent in Umm Suqeim, Dubai",
    seoTitle: "Property for Sale & Rent in Umm Suqeim Dubai | Hive Homes",
    seoDescription:
      "Explore properties for sale and rent in Umm Suqeim Dubai. Beachside neighbourhood, Burj Al Arab views, villas and Madinat Jumeirah Living. Hive Homes brokerage.",
    paragraphs: [
      "Umm Suqeim is one of Dubai's most established and desirable residential neighbourhoods, a beachside community that offers a rare combination of genuine character, proximity to the sea, and some of the city's most iconic views. The Burj Al Arab sits just offshore.",
      "The area offers traditional villa plots, newer apartment developments, and Madinat Jumeirah Living, a premium Emaar residential project that brings a contemporary option to one of Dubai's most loved postcodes.",
    ],
    statsBar: {
      propertyTypes: "Villas, Apartments",
    },
    ctaLabel: "Speak to an Umm Suqeim Specialist",
    // Brand photo of Umm Suqeim.
    fallbackCover: "/images/communities/umm-suqeim.jpg",
  },
  {
    slug: "the-valley",
    name: "The Valley",
    blurb:
      "Emaar's family-focused community. Open spaces, green parks, and village-style living.",
    highlight: "Family townhouses",
    pageHeadline: "Properties for Sale in The Valley, Dubai",
    seoTitle: "Property for Sale in The Valley Dubai by Emaar | Hive Homes",
    seoDescription:
      "Browse villas and townhouses in The Valley Dubai by Emaar. Family community with parks, sports facilities and open green spaces on the Dubai–Al Ain Road. Hive Homes.",
    paragraphs: [
      "The Valley is Emaar's answer to families searching for space, greenery, and a genuine sense of community. Located on the Dubai–Al Ain Road, the development centres around a town centre, sports facilities, a kids' dale, and kilometres of open parks.",
      "Villas and townhouses are available across several sub-communities within The Valley, each with its own character and price point. For families relocating to Dubai or looking to upsize from an apartment, The Valley offers one of the most complete community environments currently available.",
    ],
    statsBar: {
      propertyTypes: "Villas, Townhouses",
    },
    ctaLabel: "Speak to a Valley Specialist",
    // Suburban-luxury family villa with garden — closest match for The
    // Valley's family-community positioning.
    fallbackCover:
      "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=1600&q=80",
  },
  {
    slug: "dubai-harbour",
    name: "Dubai Harbour",
    blurb:
      "Waterfront living at its finest, superyacht marina and Emaar Beachfront access.",
    highlight: "Yacht marina",
    pageHeadline: "Properties for Sale in Dubai Harbour, Dubai",
    seoTitle:
      "Property for Sale in Dubai Harbour & Emaar Beachfront | Hive Homes",
    seoDescription:
      "Discover luxury apartments for sale in Dubai Harbour and Emaar Beachfront. Marina views, private beach access, waterfront living. Hive Homes Dubai boutique brokerage.",
    paragraphs: [
      "Dubai Harbour is one of the most ambitious waterfront developments in the region — a maritime destination home to the Middle East's largest marina and the Ain Dubai observation wheel. The residential offering centres on Emaar Beachfront, a collection of high-rise towers with direct beach access and stunning sea views.",
      "Properties here appeal to buyers looking for a premium waterfront lifestyle and investors who see long-term value in a district still in its early development phases.",
    ],
    statsBar: {
      propertyTypes: "Apartments, Penthouses",
    },
    ctaLabel: "Speak to a Dubai Harbour Specialist",
    // Brand photo of the Dubai Harbour marina district.
    fallbackCover: "/images/communities/dubai-harbour.jpg",
  },
  {
    slug: "downtown-dubai",
    name: "Downtown Dubai",
    blurb:
      "The city's iconic centre. Burj Khalifa, Dubai Mall and address-grade apartments.",
    highlight: "Burj Khalifa views",
    pageHeadline: "Properties for Sale & Rent in Downtown Dubai",
    seoTitle: "Property for Sale & Rent in Downtown Dubai | Hive Homes",
    seoDescription:
      "Browse luxury apartments and penthouses for sale and rent in Downtown Dubai. Burj Khalifa views, Address-branded residences. Hive Homes boutique brokerage.",
    paragraphs: [
      "Downtown Dubai is the city's most recognisable address — home to the Burj Khalifa, The Dubai Mall, the Dubai Fountain, and a tightly-edited collection of Address-, Burj- and Boulevard-branded residences. Buyers come here for views, immediacy, and a postcode that needs no introduction.",
      "Stock ranges from one-bedroom apartments on the Boulevard to full-floor penthouses with direct Burj Khalifa views. Resale prices have held a premium since launch and continue to do so; rentals are some of the most consistently in-demand in the city.",
    ],
    statsBar: {
      propertyTypes: "Apartments, Penthouses",
    },
    ctaLabel: "Speak to a Downtown Specialist",
    // Burj Khalifa skyline, centre-stage for Downtown Dubai.
    fallbackCover:
      "https://images.unsplash.com/photo-1746731341047-76b2652ea843?auto=format&fit=crop&w=1600&q=80",
  },
  {
    slug: "zabeel",
    name: "Zabeel",
    blurb:
      "Royal-adjacent district. Quiet, central, with the Dubai Frame as its landmark.",
    highlight: "Dubai Frame district",
    pageHeadline: "Properties for Sale & Rent in Zabeel, Dubai",
    seoTitle: "Property for Sale & Rent in Zabeel Dubai | Hive Homes",
    seoDescription:
      "Discover apartments for sale and rent in Zabeel Dubai. Quiet, central, royal-adjacent district close to Downtown and the Dubai Frame. Hive Homes brokerage.",
    paragraphs: [
      "Zabeel is one of Dubai's most discreet residential districts, sitting between Downtown Dubai and Bur Dubai with the Dubai Frame as its civic landmark. The area is quieter than its more famous neighbours and benefits from generous green space, including Zabeel Park, and easy access to Sheikh Zayed Road.",
      "The community is anchored by a small number of premium developments, including Wasl Tower, One Za'abeel, and a growing collection of Emaar and Wasl residences. It suits buyers who want a central location without the constant footfall of Downtown.",
    ],
    statsBar: {
      propertyTypes: "Apartments, Penthouses",
    },
    ctaLabel: "Speak to a Zabeel Specialist",
    // Brand aerial photo of the Dubai Frame at Zabeel.
    fallbackCover: "/images/communities/zabeel.jpg",
  },
  {
    slug: "dubai-land",
    name: "Dubai Land",
    blurb:
      "Dubailand — family villa corridor stretching east, anchored by Arabian Ranches and Damac Hills.",
    highlight: "Villa corridor",
    pageHeadline: "Properties for Sale & Rent in Dubailand",
    seoTitle: "Property for Sale & Rent in Dubailand | Hive Homes",
    seoDescription:
      "Browse villas, townhouses and off-plan launches across Dubailand — Arabian Ranches, Damac Hills, Mudon, Town Square and beyond. Hive Homes Dubai brokerage.",
    paragraphs: [
      "Dubailand is the umbrella name for the city's eastern villa corridor — a string of master-planned family communities stretching out along Emirates Road and Sheikh Mohammed Bin Zayed Road. Arabian Ranches, Damac Hills, Mudon, Town Square, Reem and Serena all sit inside it.",
      "Buyers choose Dubailand for space. Plot sizes here typically exceed anything you'll find in the central districts at the same price point, and the area's family infrastructure — schools, parks, sports facilities — is mature. The trade-off is commute time: Downtown is twenty to thirty minutes depending on the cluster.",
    ],
    statsBar: {
      propertyTypes: "Villas, Townhouses, Apartments",
    },
    ctaLabel: "Speak to a Dubailand Specialist",
    // Brand aerial photo of a Dubai villa community — captures the
    // family-villa positioning that defines the Dubailand corridor.
    fallbackCover: "/images/site/communities.jpg",
  },
  {
    slug: "mohammed-bin-rashid-city",
    name: "Mohammed Bin Rashid City",
    blurb:
      "MBR City — modern villa community around a crystal lagoon and 18-hole golf course.",
    highlight: "Central lagoon living",
    pageHeadline: "Properties for Sale in Mohammed Bin Rashid City, Dubai",
    seoTitle:
      "Property for Sale in Mohammed Bin Rashid City (MBR City) | Hive Homes",
    seoDescription:
      "Browse villas, townhouses and apartments for sale in Mohammed Bin Rashid City (MBR City) Dubai. Crystal lagoon, golf, central location. Hive Homes Dubai.",
    paragraphs: [
      "Mohammed Bin Rashid City, known across Dubai simply as MBR City, is a master-planned mixed-use community wrapped around the Meydan racecourse and District One's crystal lagoon. The area is positioned as a more central alternative to communities further south, with quick access to Downtown Dubai and the airport.",
      "Stock includes everything from one-bedroom apartments at Sobha Hartland to full-plot villas and contemporary mansions at District One. Buyers come here for the unusual combination of central location, generous space, and resort-style amenities — including the largest man-made lagoon in the city.",
    ],
    statsBar: {
      propertyTypes: "Villas, Townhouses, Apartments",
    },
    ctaLabel: "Speak to an MBR City Specialist",
    // Verified Dubai luxury villa exterior with pool — by an Unsplash
    // contributor (https://unsplash.com/photos/ab600027ffc6). Suits
    // MBR City's District One villa positioning until designer
    // photography lands.
    fallbackCover:
      "https://images.unsplash.com/photo-1613977257363-707ba9348227?auto=format&fit=crop&w=1600&q=80",
  },
];

export const COMMUNITIES_BY_SLUG: Record<string, CommunityContent> =
  Object.fromEntries(COMMUNITIES.map((c) => [c.slug, c]));

/**
 * The eight communities featured on the homepage, footer and the
 * `/areas` list — ordered exactly as the May 2026 content brief
 * specifies. The remaining entries in `COMMUNITIES` keep their dedicated
 * `/areas/<slug>` pages but are not surfaced in the curated lists.
 */
export const PRIMARY_COMMUNITY_SLUGS = [
  "jumeirah-village-circle",
  "arjan",
  "business-bay",
  "damac-lagoons",
  "tilal-al-ghaf",
  "umm-suqeim",
  "the-valley",
  "dubai-harbour",
] as const;

export const PRIMARY_COMMUNITIES: CommunityContent[] =
  PRIMARY_COMMUNITY_SLUGS.map((slug) => COMMUNITIES_BY_SLUG[slug]).filter(
    Boolean,
  ) as CommunityContent[];

export function findCommunityContent(
  identifier: string | undefined,
): CommunityContent | undefined {
  if (!identifier) return undefined;
  const direct = COMMUNITIES_BY_SLUG[identifier];
  if (direct) return direct;
  const lower = identifier.toLowerCase().trim();
  return COMMUNITIES.find(
    (c) => c.name.toLowerCase() === lower || c.slug === lower,
  );
}
